import React, { useState, useEffect } from 'react';
import api from '@futuredev/shared-auth/api';
import { extractApiError } from '@futuredev/shared-system/formatters';
import './AppStore.css';

interface AppStorePlugin {
    id: string;
    name: string;
    description: string;
    developer_name: string;
    icon_url: string;
    is_installed: boolean;
}

const AppStore: React.FC = () => {
    const [apps, setApps] = useState<AppStorePlugin[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchApps = async () => {
        try {
            setLoading(true);
            const response = await api.get('/marketplace/apps');
            setApps(response.data.data);
            setError(null);
        } catch (err: unknown) {
            setError(extractApiError(err, 'Failed to load App Marketplace'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApps();
    }, []);

    const toggleAppInstall = async (appId: string, isInstalled: boolean) => {
        try {
            if (isInstalled) {
                await api.post(`/marketplace/apps/${appId}/uninstall`);
            } else {
                await api.post(`/marketplace/apps/${appId}/install`);
            }
            await fetchApps();
        } catch (err: unknown) {
            alert(extractApiError(err, 'Action failed.'));
        }
    };

    if (loading) {
        return <div className="app-store-loading">Loading Marketplace...</div>;
    }

    if (error) {
        return <div className="app-store-error">{error}</div>;
    }

    return (
        <div className="app-store-container page-container">
            <div className="glass-panel">
                <div className="app-store-header">
                    <h2>Vendor App Ecosystem</h2>
                    <p>Enhance your store by installing third-party integrations.</p>
                </div>

                <div className="app-grid">
                    {apps.map((app) => (
                        <div key={app.id} className="app-card">
                            <img src={app.icon_url} alt={app.name} className="app-icon" />
                            <div className="app-details">
                                <h3>{app.name}</h3>
                                <span className="developer-tag">By {app.developer_name}</span>
                                <p>{app.description}</p>
                            </div>
                            <div className="app-actions">
                                <button
                                    className={`install-btn ${app.is_installed ? 'uninstall' : 'install'}`}
                                    onClick={() => toggleAppInstall(app.id, app.is_installed)}
                                >
                                    {app.is_installed ? 'Revoke Access' : 'Install App'}
                                </button>
                            </div>
                        </div>
                    ))}
                    {apps.length === 0 && (
                        <div className="no-apps-message">
                            No third-party apps available at this time.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppStore;
